import { useLazyQuery } from '@apollo/client'
import gql from 'graphql-tag'
import { useCallback } from 'react'

// Invoice type based on the expected response
export type Invoice = {
	invoiceId: number,
	invoiceNo: string,
	company: {
		name: string,
	},
}

export type InvoicePayload = {
	invoices: {
		invoices: Invoice[],
	},
}

// Variables type for the query
type Variables = {
	search?: string,
	allowSent?: boolean,
	allowApproved?: boolean,
	branchId?: string,
}

export const buildPath = ( { args }: {args: Variables} ) => {
	let path = `/billing-service/invoices/search?paid=false&termQuery=${encodeURIComponent( args.search ?? '' )}`

	if ( !args.allowSent ) {
		path += '&sent=false'
	}

	if ( !args.allowApproved ) {
		path += '&billingApproved=false'
	}

	if ( args.branchId ) {
		path += `&branchId=${encodeURIComponent( args.branchId )}`
	}

	return path
}

// Note: Apollo Link REST pathBuilder needs to be configured at the link level, not in the query
// For now, using a static path that covers the most common use case
export const invoicesQuery = gql`
	query InvoiceQuery($search: String, $buildPath: any!, $allowSent: Boolean, $allowApproved: Boolean, $branchId: String) {
		invoices(search: $search, allowSent: $allowSent, allowApproved: $allowApproved, branchId: $branchId)
			@rest(
				type: "InvoicePayload"
        pathBuilder: $buildPath,
				endpoint: "esapi"
			) {
			invoices {
				invoiceId
				invoiceNo
				company {
					name
				}
			}
		}
	}
`

export type UseInvoicesQueryResult = {
	searchInvoices: ( search: string, options?: { allowSent?: boolean, allowApproved?: boolean, branchId?: string } ) => void,
	invoices: Invoice[],
	loading: boolean,
	error: any,
}

/**
 * Hook for querying invoices using Apollo Link REST with custom path builder
 * Provides search functionality with optional branch ID filtering
 * Only available for company ID 1854
 */
export default function useInvoicesQuery(): UseInvoicesQueryResult {
	const [ executeQuery, { data, loading, error } ] = useLazyQuery<InvoicePayload>( invoicesQuery, {
		errorPolicy: 'all',
		fetchPolicy: 'cache-and-network',
	} )

	const searchInvoices = useCallback( ( search: string, options?: { allowSent?: boolean, allowApproved?: boolean, branchId?: string } ) => {
		if ( !search || search.length < 5 ) {
			return
		}

		executeQuery( {
			variables: {
				buildPath,
				search,
				allowSent: options?.allowSent ?? false,
				allowApproved: options?.allowApproved ?? false,
				branchId: options?.branchId ?? '',
			},
		} )
	}, [ executeQuery ] )

	return {
		searchInvoices,
		invoices: data?.invoices?.invoices ?? [],
		loading,
		error,
	}
}
